import { Menu, MenuItemOption } from 'ui/menu';
import { FilterMenuTriggerButton } from 'ui/indexPages/filters/FilterMenuTriggerButton';
import { ensureEnum } from 'utils/tsHelpers';

import {
  Filter,
  MESSAGE_MEDIUM_DISPLAY_NAMES,
  POLICY_DISPLAY_NAMES,
  SUBSCRIPTION_STATE_DISPLAY_NAMES,
} from '../constants';
import { SubscriptionPolicy } from '../../domain';
import { useQueryContext, useFiltersContext } from '../contexts';

const prefixIdWith = (id: string, prefix: string) => `${prefix}${id}`;
const prefixIdsWith = (arr: { id: string; label: string }[], prefix: string) =>
  arr.map(({ id, ...otherProperties }) => ({ id: prefixIdWith(id, prefix), ...otherProperties }));
const stripIdPrefix = (prefixedId: string, prefix: string) =>
  prefixedId.startsWith(prefix) ? prefixedId.substr(prefix.length) : prefixedId;

export const FiltersMenu = () => {
  const { channelMedia } = useQueryContext();
  const {
    subscriptionPolicies,
    toggleSubscriptionPolicy,
    subscriptionStatuses,
    toggleSubscriptionStatus,
    mediums,
    toggleMedium,
  } = useFiltersContext();

  const options: MenuItemOption[] = [
    {
      hasFilter: true,
      id: Filter.medium,
      isSelectMenu: true,
      label: 'Medium',
      options: prefixIdsWith(
        channelMedia.sort().map((messageMedium) => ({
          id: messageMedium,
          label: MESSAGE_MEDIUM_DISPLAY_NAMES[messageMedium],
        })),
        Filter.medium,
      ),
    },
    {
      hasFilter: false,
      id: Filter.policy,
      isSelectMenu: true,
      label: 'Policy',
      options: prefixIdsWith(
        Object.entries(POLICY_DISPLAY_NAMES).map(([id, label]) => ({ id, label })),
        Filter.policy,
      ),
    },
    {
      hasFilter: false,
      id: Filter.status,
      isSelectMenu: true,
      label: 'Status',
      options: prefixIdsWith(
        Object.entries(SUBSCRIPTION_STATE_DISPLAY_NAMES).map(([id, label]) => ({ id, label })),
        Filter.status,
      ),
    },
  ];

  const currentSelection = [
    ...subscriptionPolicies.map((policy) => prefixIdWith(policy, Filter.policy)),
    ...subscriptionStatuses.map((status) => prefixIdWith(`${status}`, Filter.status)),
    ...mediums.map((medium) => prefixIdWith(`${medium}`, Filter.medium)),
  ];

  const handleSelectionChange = (prefixedFilterId: string) => {
    if (prefixedFilterId.startsWith(Filter.policy)) {
      toggleSubscriptionPolicy(
        ensureEnum(
          stripIdPrefix(prefixedFilterId, Filter.policy),
          SubscriptionPolicy,
          SubscriptionPolicy.optIn,
        ),
      );
    } else if (prefixedFilterId.startsWith(Filter.status)) {
      toggleSubscriptionStatus(
        stripIdPrefix(prefixedFilterId, Filter.status).toLowerCase() === 'true',
      );
    } else if (prefixedFilterId.startsWith(Filter.medium)) {
      toggleMedium(stripIdPrefix(prefixedFilterId, Filter.medium));
    }
  };

  return (
    <Menu
      options={options}
      defaultSelectedOptionIds={currentSelection}
      id="subscriptions-filters"
      handleSelectionChange={handleSelectionChange}
    >
      <FilterMenuTriggerButton selectedFilterCount={currentSelection.length} />
    </Menu>
  );
};
